import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ResetPasswordBox,
  ResetPasswordStack,
  PasswordTextField,
  PasswordButton,
} from "./ResetPassword.styled";

import { usersApi } from "@api/services";
import { Error } from "@helpers";
import { Typography } from "@components/base";

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMatch, setPasswordMatch] = useState<boolean | undefined>(undefined);

  const handlePasswordChange = (newPassword: string): void => {
    setPassword(newPassword);
    setPasswordMatch(newPassword === confirmPassword || confirmPassword === "");
  };

  const handleConfirmPasswordChange = (newConfirmPassword: string): void => {
    setConfirmPassword(newConfirmPassword);
    setPasswordMatch(password === newConfirmPassword || newConfirmPassword === "");
  };

  const handlePasswordSave = async (): Promise<void> => {
    if (!oldPassword || !password || !confirmPassword) {
      alert(t("settings.allFieldsRequired"));

      return;
    }

    if (passwordMatch === false) {
      alert(t("settings.passwordMismatch"));

      return;
    }

    try {
      await usersApi.usersControllerChangeCurrentUserPassword({
        oldPassword: oldPassword,
        newPassword: password,
      });
      alert(t("settings.settings.password"));
      setOldPassword("");
      setPassword("");
      setConfirmPassword("");
      setPasswordMatch(undefined);
    } catch (error) {
      Error("Failed to change password", error);
      alert(t("settings.passwordChangeFailed"));
    }
  };

  const isSaveDisabled =
    !oldPassword || !password || !confirmPassword || passwordMatch === false;

  return (
    <ResetPasswordBox>
      <Typography variant="h6" gutterBottom>
        {t("settings.changePassword")}
      </Typography>
      <ResetPasswordStack direction="row" alignItems="center" spacing={2}>
        <PasswordTextField
          label={t("settings.password_old")}
          type="password"
          variant="outlined"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          autoComplete="old-password"
        />
      </ResetPasswordStack>

      <ResetPasswordStack direction="row" alignItems="center" spacing={2}>
        <PasswordTextField
          label={t("settings.password")}
          type="password"
          variant="outlined"
          value={password}
          onChange={(e) => handlePasswordChange(e.target.value)}
          match={passwordMatch}
          autoComplete="new-password"
        />
        <PasswordTextField
          label={t("settings.confirmPassword")}
          type="password"
          variant="outlined"
          value={confirmPassword}
          onChange={(e) => handleConfirmPasswordChange(e.target.value)}
          match={passwordMatch}
          autoComplete="new-password-confirm"
        />
      </ResetPasswordStack>
      <PasswordButton
        variant="contained"
        color="success"
        onClick={handlePasswordSave}
        disabled={isSaveDisabled}
      >
        {t("settings.save")}
      </PasswordButton>
    </ResetPasswordBox>
  );
};

export default ResetPassword;
